import { useEffect, useRef } from "react";

/**
 * Sign the user out after `idleMs` of no interaction.
 *
 * Watches mouse/keyboard/scroll/touch events on window. Each event bumps the
 * last-activity timestamp (throttled to once every 5s so we're not thrashing).
 * Every 30s a background interval checks the delta and fires `onIdle()` once.
 */
export function useIdleTimeout(onIdle, enabled = true, idleMs = 30 * 60 * 1000) {
  const lastActivity = useRef(Date.now());
  const firedRef = useRef(false);

  useEffect(() => {
    if (!enabled) {
      firedRef.current = false;
      return;
    }
    lastActivity.current = Date.now();
    firedRef.current = false;

    let lastBump = 0;
    const bump = () => {
      const now = Date.now();
      if (now - lastBump < 5000) return; // throttle
      lastBump = now;
      lastActivity.current = now;
      firedRef.current = false;
    };

    const events = ["mousedown", "keydown", "scroll", "touchstart", "mousemove"];
    events.forEach((e) => window.addEventListener(e, bump, { passive: true }));

    const check = setInterval(() => {
      if (firedRef.current) return;
      if (Date.now() - lastActivity.current > idleMs) {
        firedRef.current = true;
        try { onIdle(); } catch (err) { console.error("useIdleTimeout: onIdle callback threw", err); }
      }
    }, 30_000);

    return () => {
      events.forEach((e) => window.removeEventListener(e, bump));
      clearInterval(check);
    };
  }, [enabled, onIdle, idleMs]);
}
